<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header></vue-header>
    <v-main  :style="$vuetify.breakpoint.xs ? 'padding:0px!important;margin-top: 100px;' : 'padding:0px!important;margin-top: 140px;'">
      <v-container fluid style="margin-bottom:150px;" :class=" this.$vuetify.breakpoint.xs  ? 'px-0 py-0' : 'px-6 py-6'">
        <v-row class="mt-3" style="margin: 0px;">
          <v-col cols="12" class="text-center">
            <h1 class="text-h3 font-weight-bold mb-4">{{ wheelSettings.title }}</h1>
            <p class="subtitle-1 mb-6">Spin the wheel for a chance to win amazing prizes!</p>
          </v-col>
        </v-row>

        <v-row style="margin: 0px;">
          <v-col cols="12" md="8" offset-md="2" class="text-center">
            <v-card class="card-shadow border-radius-xl">
              <v-card-text class="text-center py-6">
                <div v-if="loading" class="text-center py-8">
                  <clip-loader :loading="loading" color="#1D75F4"></clip-loader>
                  <p class="mt-4">Loading the wheel...</p>
                </div>

                <div v-else-if="!wheelSettings.active" class="text-center py-8">
                  <v-icon size="64" color="grey">mdi-timer-sand-empty</v-icon>
                  <h2 class="text-h5 mt-4">The spinning wheel is currently unavailable.</h2>
                  <p class="mt-2">Please check back later!</p>
                </div>

                <div v-else-if="wheelSettings.requireLogin && !isLoggedIn" class="text-center py-8">
                  <v-icon size="64" color="primary">mdi-account-lock</v-icon>
                  <h2 class="text-h5 mt-4">Please log in to spin the wheel</h2>
                  <v-btn color="primary" class="mt-4" :to="'/login'">
                    Log In
                  </v-btn>
                </div>

                <div v-else-if="remainingSpins === 0" class="text-center py-8">
                  <v-icon size="64" color="amber">mdi-timer-sand</v-icon>
                  <h2 class="text-h5 mt-4">You've used all your spins for now</h2>
                  <p class="mt-2">
                    {{ getSpinLimitMessage() }}
                  </p>
                </div>

                <div v-else>
                  <spinning-wheel
                    :segments="wheelSegments"
                    :colors="wheelColors"
                    @prize-won="onPrizeWon"
                  ></spinning-wheel>

                  <div v-if="wheelSettings.spinLimit > 0" class="mt-4">
                    <div v-if="remainingSpins === null || refreshingSpins" class="d-flex align-center justify-center">
                      <clip-loader :loading="true" color="#1D75F4" :size="16" class="mr-2"></clip-loader>
                      <p class="subtitle-2 mb-0">Loading remaining spins...</p>
                    </div>

                    <div v-else class="d-flex align-center justify-center">
                      <p class="subtitle-2 mb-0 mr-2">
                        You have {{ remainingSpins }} spin{{ remainingSpins !== 1 ? 's' : '' }} remaining
                        {{ wheelSettings.spinLimitPeriod !== 'forever' ? 'today' : '' }}
                      </p>
                      <v-btn
                        icon
                        x-small
                        @click="fetchRemainingSpins"
                        title="Refresh remaining spins"
                        :loading="refreshingSpins"
                      >
                        <v-icon small>mdi-refresh</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-6" style="margin: 0px;">
          <v-col cols="12" md="8" offset-md="2">
            <v-card class="card-shadow border-radius-xl">
              <v-card-title class="d-flex align-center">
                Recent Winners
                <v-spacer></v-spacer>
                <v-btn icon small @click="loadRecentWinners" :loading="loadingWinners" title="Refresh winners">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <div v-if="loadingWinners" class="text-center py-4">
                  <clip-loader :loading="loadingWinners" color="#1D75F4" :size="20"></clip-loader>
                  <p class="mt-2">Loading recent winners...</p>
                </div>

                <div v-else-if="recentWinners.length === 0" class="text-center py-4">
                  <v-icon size="48" color="grey">mdi-trophy-outline</v-icon>
                  <p class="mt-2">No winners yet. Be the first to win!</p>
                </div>

                <v-list v-else>
                  <v-list-item v-for="(winner, index) in recentWinners" :key="index">
                    <v-list-item-avatar>
                      <v-avatar color="blue" size="40">
                        <span class="white--text">{{ winner.name.charAt(0) }}</span>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ winner.name }}</v-list-item-title>
                      <v-list-item-subtitle>
                        Won {{ winner.prize }} on {{ formatDate(winner.date) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Prize Won Dialog -->
      <v-dialog v-model="prizeDialog" max-width="500">
        <v-card>
          <v-card-title class="headline text-center">
            <v-icon large color="amber darken-2" class="mr-2">mdi-trophy</v-icon>
            Congratulations!
          </v-card-title>
          <v-card-text class="text-center">
            <div class="prize-content py-4">
              <h2 class="text-h4 mb-4">You won:</h2>
              <div class="prize-display mb-4">
                {{ currentPrize ? currentPrize.label : '' }}
              </div>

              <p v-if="currentPrize && currentPrize.description" class="mb-4">
                {{ currentPrize.description }}
              </p>

              <template v-if="currentPrize && currentPrize.type === 'discount'">
                <v-alert type="success" outlined>
                  Use code <strong>{{ currentPrize.code }}</strong> at checkout
                </v-alert>
              </template>

              <template v-if="currentPrize && currentPrize.type === 'gift_card'">
                <v-alert type="success" outlined>
                  Your gift card code: <strong>{{ giftCardCode }}</strong>
                </v-alert>
              </template>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn dark color="green" @click="closePrizeDialog">
              Awesome!
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer></vue-footer>
  </v-app>
</template>

<script>
import SpinningWheel from '@/components/Games/SpinningWheel.vue';
import ClipLoader from 'vue-spinner/src/PulseLoader.vue';
import moment from 'moment';

export default {
  name: 'SpinningWheelGame',
  components: {
    SpinningWheel,
    'RotateLoader': ClipLoader
  },
  data() {
    return {
      loading: true,
      wheelSegments: [],
      wheelColors: [],
      wheelSettings: {
        title: 'Spin to Win',
        spinDuration: 5,
        spinLimit: 1,
        spinLimitPeriod: 'day',
        requireLogin: true,
        active: true
      },

      // User state
      isLoggedIn: false,
      user: null,
      remainingSpins: null,

      // Prize dialog
      prizeDialog: false,
      currentPrize: null,
      giftCardCode: '',

      // Recent winners (for display)
      recentWinners: [],
      loadingWinners: false,
      refreshingSpins: false
    };
  },
  created() {
    this.checkLoginStatus();
    this.loadWheelData();
    this.loadRecentWinners();
  },
  methods: {
    checkLoginStatus() {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      this.isLoggedIn = !!token;

      if (this.isLoggedIn) {
        this.loadUserData();
        // Fetch remaining spins immediately
        this.fetchRemainingSpins();
      } else {
        // Set default remaining spins for non-logged in users
        this.remainingSpins = 0;
      }
    },

    loadUserData() {
      // Fetch user data from the server
      axios.get('/api/user', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(response => {
          this.user = response.data;
          // After loading user data, fetch the remaining spins
          this.fetchRemainingSpins();
        })
        .catch(error => {
          console.error('Error loading user data:', error);
        });
    },

    fetchRemainingSpins() {
      // Set loading state
      this.refreshingSpins = true;

      // Fetch remaining spins directly from the API
      if (this.isLoggedIn) {
        axios.get('/api/spinning-wheel/user-spins', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(response => {
            if (response.data.flag === 1) {
              // Set remaining spins from API response
              if (response.data.remaining_spins !== null && response.data.remaining_spins !== undefined) {
                this.remainingSpins = response.data.remaining_spins;
              } else {
                // Fallback to calculating remaining spins locally
                this.checkRemainingSpins();
              }
            } else {
              // Fallback to calculating remaining spins locally
              this.checkRemainingSpins();
            }
          })
          .catch(error => {
            console.error('Error fetching remaining spins:', error);
            // Fallback to calculating remaining spins locally
            this.checkRemainingSpins();
          })
          .finally(() => {
            this.refreshingSpins = false;
          });
      } else {
        // Not logged in, use default spin limit
        this.remainingSpins = this.wheelSettings.spinLimit || 0;
        this.refreshingSpins = false;
      }
    },

    checkRemainingSpins() {
      if (!this.user || !this.wheelSettings) return;

      // If no limit, user has unlimited spins
      if (this.wheelSettings.spinLimit === 0) {
        this.remainingSpins = Infinity;
        return;
      }

      // Check if user has spins data
      if (!this.user.spins) {
        this.remainingSpins = this.wheelSettings.spinLimit;
        return;
      }

      // Check if spin limit period has passed
      const lastSpin = new Date(this.user.spins.last_spin);
      const now = new Date();
      let resetSpins = false;

      switch (this.wheelSettings.spinLimitPeriod) {
        case 'day':
          resetSpins = !this.isSameDay(lastSpin, now);
          break;
        case 'week':
          resetSpins = !this.isSameWeek(lastSpin, now);
          break;
        case 'month':
          resetSpins = !this.isSameMonth(lastSpin, now);
          break;
        case 'forever':
          resetSpins = false;
          break;
      }

      if (resetSpins) {
        this.remainingSpins = this.wheelSettings.spinLimit;
      } else {
        this.remainingSpins = Math.max(0, this.wheelSettings.spinLimit - this.user.spins.count);
      }
    },

    loadWheelData() {
      this.loading = true;

      // Fetch the wheel configuration from the API
      axios.get('/api/spinning-wheel/config')
        .then(response => {
          if (response.data.flag === 1) {
            // If data exists, use it
            if (response.data.segments && response.data.segments.length > 0) {
              this.wheelSegments = response.data.segments;
            } else {
              // Use default segments if none exist in the database
              this.wheelSegments = [
                {
                  label: '10% OFF',
                  type: 'discount',
                  value: 10,
                  code: 'SPIN10',
                  probability: 0.25,
                  color: '#FF6384',
                  description: '10% discount on your next purchase'
                },
                {
                  label: '25% OFF',
                  type: 'discount',
                  value: 25,
                  code: 'SPIN25',
                  probability: 0.15,
                  color: '#36A2EB',
                  description: '25% discount on your next purchase'
                },
                {
                  label: '50 Points',
                  type: 'points',
                  value: 50,
                  probability: 0.2,
                  color: '#FFCE56',
                  description: '50 reward points added to your account'
                },
                {
                  label: '$10 Gift Card',
                  type: 'gift_card',
                  value: 10,
                  code: 'GIFT',
                  probability: 0.1,
                  color: '#4BC0C0',
                  description: '$10 gift card for your next purchase'
                },
                {
                  label: 'Free Item',
                  type: 'custom',
                  description: 'Get a free item with your next purchase!',
                  probability: 0.15,
                  color: '#9966FF'
                },
                {
                  label: 'Try Again',
                  type: 'custom',
                  description: 'Better luck next time!',
                  probability: 0.15,
                  color: '#FF9F40'
                }
              ];
            }

            // Load settings if they exist
            if (response.data.settings) {
              this.wheelSettings = response.data.settings;
            }

            // Set wheel colors
            this.wheelColors = this.wheelSegments.map(segment => segment.color);

            // Only check remaining spins if we haven't fetched them from the API yet
            if (this.remainingSpins === null) {
              this.checkRemainingSpins();
            }
          } else {
            // Show error message
            console.error('Error loading wheel data:', response.data.message);
          }
        })
        .catch(error => {
          console.error('Error loading wheel data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    loadRecentWinners() {
      // Set loading state
      this.loadingWinners = true;

      // Fetch recent winners from the API
      axios.get('/api/spinning-wheel/winners')
        .then(response => {
          if (response.data.flag === 1 && response.data.winners) {
            // Transform the data to match our format
            this.recentWinners = response.data.winners.map(winner => ({
              name: winner.name || 'Anonymous',
              prize: winner.prize,
              date: new Date(winner.date)
            }));
          }
        })
        .catch(error => {
          console.error('Error loading recent winners:', error);
        })
        .finally(() => {
          this.loadingWinners = false;
        });
    },
    onPrizeWon(prize) {
      console.log('Prize won:', prize);

      this.currentPrize = prize;

      // Generate a gift card code if needed
      if (prize.type === 'gift_card') {
        this.giftCardCode = this.generateGiftCardCode(prize.code);
      }

      // Temporarily decrement remaining spins for immediate feedback
      // The actual value will be updated from the API response
      if (this.remainingSpins !== Infinity && this.remainingSpins > 0) {
        this.remainingSpins--;
      }

      // Record the prize in the database if user is logged in
      if (this.isLoggedIn) {
        axios.post('/api/spinning-wheel/prize', {
          prize: prize,
          gift_card_code: this.giftCardCode
        }, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
          .then(response => {
            if (response.data.flag === 1) {
              // Update user data
              if (this.user && this.user.spins) {
                this.user.spins.count++;
                this.user.spins.last_spin = new Date();
              }

              // Update remaining spins if provided in the response
              if (response.data.remaining_spins !== null && response.data.remaining_spins !== undefined) {
                this.remainingSpins = response.data.remaining_spins;
              }

              // Refresh recent winners list
              this.loadRecentWinners();
            } else {
              console.error('Error recording prize:', response.data.message);
            }
          })
          .catch(error => {
            console.error('Error recording prize:', error);
          });
      }

      // Show the prize dialog
      this.prizeDialog = true;
    },

    closePrizeDialog() {
      this.prizeDialog = false;
      this.currentPrize = null;
      this.giftCardCode = '';

      // Refresh remaining spins after closing the dialog
      if (this.isLoggedIn) {
        this.fetchRemainingSpins();
      }
    },

    generateGiftCardCode(prefix) {
      // Generate a random code with the given prefix
      const randomPart = Math.random().toString(36).substring(2, 10).toUpperCase();
      return `${prefix}-${randomPart}`;
    },

    getSpinLimitMessage() {
      switch (this.wheelSettings.spinLimitPeriod) {
        case 'day':
          return 'Come back tomorrow for more spins!';
        case 'week':
          return 'Come back next week for more spins!';
        case 'month':
          return 'Come back next month for more spins!';
        case 'forever':
          return 'You\'ve used all your available spins.';
        default:
          return 'Come back later for more spins!';
      }
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY');
    },

    isSameDay(date1, date2) {
      return moment(date1).isSame(moment(date2), 'day');
    },

    isSameWeek(date1, date2) {
      return moment(date1).isSame(moment(date2), 'week');
    },

    isSameMonth(date1, date2) {
      return moment(date1).isSame(moment(date2), 'month');
    }
  }
};
</script>

<style scoped>
.prize-content {
  text-align: center;
  padding: 20px 0;
}

.prize-display {
  font-size: 28px;
  font-weight: bold;
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  color: #4CAF50;
}
</style>
